<template lang="pug">
component(
  v-if="registeredRenderer?.editComponent",
  :is="registeredRenderer.editComponent",
  v-model="model",
  :prop="prop",
  :disabled="props.disabled"
)
template(v-else-if="prop.type === 'text'")
  el-select(
    v-if="prop.constraint?.enumValues",
    v-model="model",
    :disabled="props.disabled"
  )
    el-option(
      v-for="option in prop.constraint.enumValues",
      :key="option",
      :value="option"
    )

  ValueListSelect(
    v-else-if="prop.constraint?.settings?.inputValue",
    v-model="model",
    :property="prop",
    :disabled="props.disabled"
  )

  el-date-picker(
    v-else-if="prop.constraint?.type === 'date'",
    v-model="model",
    type="date",
    value-format="YYYY-MM-DD",
    :format="datePickerFormat",
    :disabled="props.disabled"
  )
  el-date-picker(
    v-else-if="prop.constraint?.type === 'year'",
    v-model="model",
    type="year",
    value-format="YYYY",
    :disabled="props.disabled"
  )
  DurationPicker(
    v-else-if="prop.constraint?.type === 'duration'",
    v-model="model",
    :disabled="props.disabled",
    :unit-options="prop.constraint?.settings?.durationInputs"
  )
  //- NOTE: boolean is saved as string, either 'true' or 'false'
  el-switch(
    v-else-if="prop.constraint?.type === 'boolean'",
    v-model="model",
    active-value="true",
    inactive-value="false",
    :disabled="props.disabled"
  )

  .flex(v-else)
    el-input(
      v-if="props.hideQrInput || prop.constraint?.settings?.textOptions?.type === 'textarea'",
      v-model="model",
      type="textarea",
      :rows="prop.constraint?.settings?.textOptions?.rows ?? 1",
      :maxlength="prop.constraint?.settings?.textOptions?.maxLength",
      :show-word-limit="prop.constraint?.settings?.textOptions?.maxLength !== undefined"
    )
    QRInput(
      v-else,
      v-model="model",
      :rows="1",
      :disabled="props.disabled",
      clearable
    )

UserSelect(
  v-else-if="prop.type === 'userRelationArray'",
  v-model="model",
  :multiple="prop.constraint?.settings?.multiple",
  :disabled="props.disabled"
)
LinkPropertyInput(
  v-else-if="isLinkProperty",
  :model-value="linkModel",
  :disabled="props.disabled",
  @update:model-value="(value) => (linkModel = value)"
)
//- TODO: props in label are currently hardcoded, should be taken from templateor constraint settings https://github.com/inctec/smx-portal/issues/523
TwinSelect(
  v-else-if="prop.type === 'twinRelation'",
  v-model="twinRelationModel",
  :query="{ templateIds: prop.constraint.settings.relation.templateIds, isTemplate: false, types: prop.constraint.settings.relation.types, active: true }",
  :props-in-label="['location', 'serialNumber']",
  :multiple="prop.constraint?.settings?.multiple",
  :emit-ids="true",
  :disabled="props.disabled"
)
el-input-number(
  v-else-if="prop.type === 'float' && !prop.constraint?.enumValues",
  v-model="model",
  :disabled="props.disabled"
)
</template>

<script setup lang="ts">
import {
  isLinkPropertyValue,
  type TwinProperty,
} from "@shared/services/twinManagement";
import { useTwinStore } from "@shared/stores/twinManagement";
import {
  normalizeTwinFieldValue,
  useTwinFieldStore,
} from "@shared/stores/twinField";
import { getRenderer } from "@shared/services/propertyRendererRegistry";
import { computed, onMounted, onUnmounted, watch } from "vue";
import UserSelect from "./UserSelect.vue";
import TwinSelect from "./TwinSelect.vue";
import ValueListSelect from "./ValueListSelect.vue";
import QRInput from "@shared/components/elements/QRInput.vue";
import DurationPicker from "@shared/components/DurationPicker.vue";
import LinkPropertyInput from "./LinkPropertyInput.vue";
import {
  formatDateTime,
  formatDate,
  datePickerFormat,
} from "../../../util/formatting";

const props = defineProps<{
  prop: TwinProperty;
  modelValue: any;
  disabled?: boolean;
  hideQrInput?: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);
const twinFieldStore = useTwinFieldStore();

let unregisterField: (() => void) | undefined;

function registerField() {
  unregisterField?.();
  unregisterField = twinFieldStore.registerField(props.prop, props.modelValue);
}

onMounted(registerField);

onUnmounted(() => {
  unregisterField?.();
});

watch(
  () => props.prop,
  () => {
    registerField();
  },
);

watch(
  () => props.modelValue,
  (value) => {
    twinFieldStore.setFieldValue(props.prop, value);
  },
);

const model = computed({
  get() {
    return normalizeTwinFieldValue(props.modelValue);
  },
  set(val) {
    const value = normalizeTwinFieldValue(val);
    twinFieldStore.setFieldValue(props.prop, value);
    if (props.prop.type === "twinRelation" && value === undefined) {
      emit("update:modelValue", []);
      return;
    }
    if (
      props.prop.type === "float" &&
      value !== undefined &&
      typeof value !== "number"
    ) {
      console.error("TwinPropertyInput: Expected number for float type", value);
      return;
    }
    emit("update:modelValue", value);
  },
});

const twinRelationModel = computed({
  get() {
    if (
      props.prop.constraint?.settings?.multiple ||
      model.value === undefined ||
      Array.isArray(model.value)
    ) {
      return model.value;
    }
    return [model.value];
  },
  set(value) {
    model.value = props.prop.constraint?.settings?.multiple
      ? value
      : Array.isArray(value)
        ? value[0]
        : value;
  },
});

const linkModel = computed({
  get() {
    const value = model.value;
    if (isLinkPropertyValue(value)) {
      return value;
    }
    return undefined;
  },
  set(value) {
    model.value = value;
  },
});

const isLinkProperty = computed(
  () =>
    props.prop.type === "json" &&
    (props.prop.constraint?.type === "link" ||
      isLinkPropertyValue(model.value)),
);

const twinStore = useTwinStore();
const registeredRenderer = computed(() => {
  const templatePropUuid = props.prop.propertyTemplateUuid ?? props.prop.uuid;
  const twinType = twinStore.templateByPropertyUuid.get(templatePropUuid);
  return getRenderer(twinType, props.prop.customId);
});
</script>
